import { createSortedHeaders, sortRowKeys, extractRowKeyName } from "./rawDataSorters";
import { DataMatrix, EegDataRecord, SelectedFilterState, EEGRecording, PairFrequencyBand } from "../types";
import { groupBy } from "lodash";

export const calculateGlobalMeanZscore = (eegData: Array<EegDataRecord>) => {
    const values = eegData.map((item) => Math.abs(Number(item.value)));
    if (values.length === 0) return 0;
    const total = values.reduce((sum, value) => sum + value, 0);
    return total / values.length;
};

export const getColumnKey = (record: EegDataRecord): string | null => {
    if (record.frequency_band) return record.frequency_band.name;
    if (record.frequency_individual_value) {
        return record.frequency_individual_value?.name_internal || record.frequency_individual_value?.name_external || "Unknown";
    }
    const pairFreqBands = record.pair_frequency_bands;
    if (pairFreqBands && typeof pairFreqBands === "object") {
        const bandOne = pairFreqBands.frequency_band_one;
        const bandTwo = pairFreqBands.frequency_band_two;
        if (bandOne && bandOne.name && bandTwo && bandTwo.name) {
            return `${bandOne.name} / ${bandTwo.name}`;
        }
    }
    return "Value"; // Fallback for metrics without a frequency dimension
};

export const createRawDataMatrix = (records: Array<EegDataRecord>): DataMatrix => {
    if (!records || records.length === 0) {
        return { headers: [], rows: {} };
    }

    const rows: Record<string, Record<string, string>> = {};
    const allHeaders = createSortedHeaders(records);
    const allRowKeys = sortRowKeys(records);

    // Initialize rows with all possible keys to maintain order
    allRowKeys.forEach((key) => {
        rows[key] = {};
    });

    records.forEach((record) => {
        const rowKey = extractRowKeyName(record);
        const colKey = getColumnKey(record);

        if (rowKey && colKey) {
            if (!rows[rowKey]) {
                rows[rowKey] = {};
            }
            rows[rowKey][colKey] = parseFloat(record.value).toFixed(6);
        }
    });

    // Ensure all rows have all headers
    Object.keys(rows).forEach((rowKey) => {
        allHeaders.forEach((header) => {
            if (!rows[rowKey][header]) {
                rows[rowKey][header] = "N/A";
            }
        });
    });

    return {
        headers: allHeaders,
        rows: rows,
    };
};

type FilterCondition = {
    _or?: Array<Record<string, any>>;
    _and?: Array<Record<string, any>>;
    [key: string]: any;
};

export const constructEegDataFilter = (
    recordingId: number | string,
    selectedFilter: SelectedFilterState
): Array<FilterCondition> => {
    if (!recordingId) {
        throw new Error("Recording ID is required");
    }
    const dataFilter = [
        {
            eeg_recording: {
                id: {
                    _eq: recordingId,
                },
            },
        },
    ] as FilterCondition[];

    if (selectedFilter.band.length > 0) {
        dataFilter.push({
            _or: [
                { frequency_band: { id: { _in: selectedFilter.band } } },
                {
                    _and: [
                        { pair_frequency_bands: { frequency_band_one: { id: { _in: selectedFilter.band } } } },
                        { pair_frequency_bands: { frequency_band_two: { id: { _in: selectedFilter.band } } } },
                    ],
                },
            ],
        });
    }

    if (selectedFilter.individual.length > 0) {
        dataFilter.push({
            frequency_individual_value: {
                id: {
                    _in: selectedFilter.individual,
                },
            },
        });
    }

    return dataFilter;
};

// Positive means improvement, negative means worsening
export const calculateImprovement = (firstEEGMetricMean: number, secondEEGMetricMean: number) => {
    return Number((firstEEGMetricMean - secondEEGMetricMean).toFixed(2));
};

export const groupByFrequencyBand = (data: Array<EegDataRecord>) => {
    return groupBy(data, (record: EegDataRecord) => {
        if (record.frequency_band?.name) {
            return record.frequency_band.name;
        } else if (record.frequency_individual_value?.name_internal) {
            return record.frequency_individual_value.name_internal;
        } else if (
            record.pair_frequency_bands &&
            typeof record.pair_frequency_bands === "object" &&
            record.pair_frequency_bands.frequency_band_one &&
            record.pair_frequency_bands.frequency_band_two
        ) {
            return `${record.pair_frequency_bands.frequency_band_one.name} / ${record.pair_frequency_bands.frequency_band_two.name}`;
        }
        return "Undefined";
    });
};

export const groupByBrainRegion = (data: Array<EegDataRecord>) => {
    return groupBy(data, (record: EegDataRecord) => {
        if (record.single_electrode_location?.brain_region) {
            return record.single_electrode_location.brain_region;
        } else if (
            record.pair_electrode_location &&
            record.pair_electrode_location.electrode_location_one?.brain_region &&
            record.pair_electrode_location.electrode_location_two?.brain_region
        ) {
            const regionOne = record.pair_electrode_location.electrode_location_one.brain_region;
            const regionTwo = record.pair_electrode_location.electrode_location_two.brain_region;
            return regionOne === regionTwo ? regionOne : `${regionOne}-${regionTwo}`;
        }
        return "Undefined";
    });
};

// Utility function to calculate expected rows and detect mismatches for EEG data
export const calculateExpectedRowsAndMismatch = (
    data: Array<EegDataRecord>,
    recording: EEGRecording,
    selectedFilter: SelectedFilterState,
    bandFrequencyPairs: Array<PairFrequencyBand>,
    metricId: number
) => {
    const montage = recording.montage ? parseInt(recording.montage, 10) : 0;
    const isElectrodePairMetric = data?.length > 0 && data[0].metric && data[0].metric.category === "connectivity";
    const isBandPairMetric = metricId === 5;
    const filterCount =
        selectedFilter.band.length > 0
            ? selectedFilter.band.length
            : selectedFilter.individual.length > 0
            ? selectedFilter.individual.length
            : 1;
    const bandPairCount = isBandPairMetric ? bandFrequencyPairs.length : filterCount;
    const electrodeCount = isElectrodePairMetric ? (montage * (montage - 1)) / 2 : montage;
    const expectedRows = electrodeCount * bandPairCount;
    const hasDataMismatch = montage > 0 && expectedRows > 0 && data?.length !== expectedRows;

    return {
        expectedRows,
        actualRows: data?.length || 0,
        hasDataMismatch,
    };
};
